// Browser-only PDF export for an itinerary using jsPDF.
import { jsPDF } from "jspdf";

export type PdfTemplate = "classic" | "colorful" | "magazine" | "minimal";

export type PdfStop = {
  index: number;
  name: string;
  blurb?: string;
  arrive?: string; // HH:MM
  leave?: string;  // HH:MM
  note?: string;
  mapsUrl?: string;
  lat?: number;
  lng?: number;
};

export type PdfBreak = {
  label: string;
  start?: string;
  end?: string;
  venue?: {
    name: string;
    subtitle?: string;
    blurb?: string;
    priceLabel?: string;
    mapsUrl?: string;
    website?: string;
    phone?: string;
    lat?: number;
    lng?: number;
  };
};

export type PdfItem =
  | ({ type: "stop" } & PdfStop)
  | ({ type: "break" } & PdfBreak);

export type PdfDaySection = {
  /** "Dia 1", "Dia 2", ... */
  title: string;
  dateLabel?: string;
  /** Static map rendered for THIS day only. */
  mapImageDataUrl?: string;
  /** Google Maps directions link for THIS day only. */
  routeUrl?: string;
  /** "Distancia: 4.2 km - Deslocacao: 1 h 10 min" */
  summary?: string;
  /** Stops kept in the list but impossible to place on the map. */
  unmappedNames?: string[];
  items: PdfItem[];
};

export type PdfData = {
  city: string;
  country?: string;
  dateLabel?: string;       // e.g. "16 jun 2026"
  startTime?: string;       // HH:MM
  modeLabel?: string;       // Carro / A pé / ...
  totalDistanceLabel?: string;
  totalTravelLabel?: string;
  totalCostLabel?: string;
  items: PdfItem[];
  /** When present, each day is rendered with its own complete route + map. */
  days?: PdfDaySection[];
  footer?: string;
  template?: PdfTemplate;
  fullRouteUrl?: string;
  mapImageDataUrl?: string;
  copyBaseUrl?: string;     // e.g. "https://routify-tour.com/copy"
  /** Links oficiais de mobilidade (passes, estacionamento, portagens, ZTL...). */
  resources?: {
    title: string;
    note?: string;
    entries: Array<{ group: string; label: string; url: string; note?: string }>;
  };
};

const MARGIN = 14;
const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;

function drawRouteMap(doc: jsPDF, data: PdfData, th: Theme, y: number): number {
  const stops = data.items.filter((item): item is ({ type: "stop" } & PdfStop) => item.type === "stop");
  if (stops.length < 2) return y;

  const mapH = data.mapImageDataUrl ? 78 : 26;
  if (y + mapH > PAGE_H - 20) {
    doc.addPage();
    y = MARGIN;
  }

  const x = MARGIN;
  const w = CONTENT_W;
  const top = y;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(...th.cardBorder);
  doc.roundedRect(x, top, w, mapH, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text("Mapa da rota", x + 4, top + 7);
  if (data.fullRouteUrl) {
    doc.setTextColor(...th.accent);
    doc.setFontSize(8);
    doc.textWithLink("Abrir mapa completo >", x + w - 4, top + 7, { url: data.fullRouteUrl, align: "right" });
  }

  if (data.mapImageDataUrl) {
    const imageX = x + 4;
    const imageY = top + 11;
    const imageW = w - 8;
    const imageH = 56;
    doc.addImage(data.mapImageDataUrl, "JPEG", imageX, imageY, imageW, imageH, undefined, "FAST");
    doc.setDrawColor(...th.cardBorder);
    doc.rect(imageX, imageY, imageW, imageH, "S");
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Mapa real indisponível neste download. Usa o link para abrir a rota completa.", x + 4, top + 16);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  const names = stops.slice(0, 5).map((s) => `${s.index}. ${sanitize(s.name)}`).join("   ");
  const suffix = stops.length > 5 ? `   +${stops.length - 5}` : "";
  doc.text(doc.splitTextToSize(`${names}${suffix}`, w - 8), x + 4, top + mapH - 5);
  const venueCount = data.items.filter((item) => item.type === "break" && item.venue?.lat != null && item.venue?.lng != null).length;
  if (venueCount > 0) {
    doc.setTextColor(...th.accent);
    doc.text(`R = restaurante/refeição · H = hotel`, x + w - 4, top + mapH - 5, { align: "right" });
  }
  doc.setLineWidth(0.2);
  return y + mapH + 6;
}

const TRANSLIT: Record<string, string> = {
  "Ș": "S", "ș": "s", "Ş": "S", "ş": "s",
  "Ț": "T", "ț": "t", "Ţ": "T", "ţ": "t",
  "Ă": "A", "ă": "a", "Â": "A", "â": "a", "Î": "I", "î": "i",
  "Č": "C", "č": "c", "Ć": "C", "ć": "c",
  "Đ": "D", "đ": "d", "Ď": "D", "ď": "d",
  "Ě": "E", "ě": "e", "Ę": "E", "ę": "e",
  "Ğ": "G", "ğ": "g",
  "Ł": "L", "ł": "l", "Ľ": "L", "ľ": "l",
  "Ń": "N", "ń": "n", "Ň": "N", "ň": "n",
  "Ő": "O", "ő": "o",
  "Ř": "R", "ř": "r",
  "Ś": "S", "ś": "s", "Š": "S", "š": "s",
  "Ű": "U", "ű": "u",
  "Ý": "Y", "ý": "y",
  "Ź": "Z", "ź": "z", "Ż": "Z", "ż": "z", "Ž": "Z", "ž": "z",
  "→": ">", "←": "<", "•": "-", "·": "-", "–": "-", "—": "-",
  "“": '"', "”": '"', "‘": "'", "’": "'",
};

function sanitize(s: string): string {
  if (!s) return "";
  let out = s.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F1E6}-\u{1F1FF}]/gu, "");
  // eslint-disable-next-line no-control-regex -- Latin-1 range check for the PDF font
  out = out.replace(/[^\x00-\xFF]/g, (ch) => TRANSLIT[ch] ?? "?");
  return out.replace(/\s+/g, " ").trim();
}

type RGB = [number, number, number];
type Theme = {
  headerBg: RGB;
  headerText: RGB;
  accent: RGB;          // badge / dividers / link
  breakBg: RGB;
  breakBorder: RGB;
  breakText: RGB;
  cardBorder: RGB;
  cardFill?: RGB;       // optional inner fill
  sideStripe?: boolean; // magazine: vertical accent stripe in card
  title: string;        // brand label
  uppercase?: boolean;  // titles in uppercase (minimal)
};

const THEMES: Record<PdfTemplate, Theme> = {
  classic: {
    headerBg: [15, 23, 42], headerText: [255, 255, 255],
    accent: [37, 99, 235],
    breakBg: [254, 243, 199], breakBorder: [252, 211, 77], breakText: [120, 53, 15],
    cardBorder: [226, 232, 240],
    title: "Routify",
  },
  colorful: {
    headerBg: [124, 58, 237], headerText: [255, 255, 255],
    accent: [236, 72, 153],
    breakBg: [255, 237, 213], breakBorder: [251, 146, 60], breakText: [124, 45, 18],
    cardBorder: [233, 213, 255], cardFill: [250, 245, 255],
    title: "Routify",
  },
  magazine: {
    headerBg: [12, 74, 110], headerText: [255, 255, 255],
    accent: [20, 184, 166],
    breakBg: [224, 242, 254], breakBorder: [14, 165, 233], breakText: [12, 74, 110],
    cardBorder: [203, 213, 225], cardFill: [248, 250, 252],
    sideStripe: true,
    title: "ROUTIFY",
  },
  minimal: {
    headerBg: [255, 255, 255], headerText: [24, 24, 27],
    accent: [24, 24, 27],
    breakBg: [250, 250, 250], breakBorder: [212, 212, 216], breakText: [39, 39, 42],
    cardBorder: [228, 228, 231],
    title: "ROUTIFY",
    uppercase: true,
  },
};

function drawItems(
  doc: jsPDF,
  items: PdfItem[],
  data: PdfData,
  th: Theme,
  tpl: PdfTemplate,
  startY: number,
): number {
  let y = startY;
  for (const item of items) {
    if (y > PAGE_H - 30) { doc.addPage(); y = MARGIN; }

    if (item.type === "break") {
      if (y + 12 > PAGE_H - 16) { doc.addPage(); y = MARGIN; }
      doc.setFillColor(...th.breakBg);
      doc.setDrawColor(...th.breakBorder);
      const headerH = 10;
      doc.roundedRect(MARGIN, y, CONTENT_W, headerH, 2, 2, "FD");
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...th.breakText);
      const timeStr = [item.start, item.end].filter(Boolean).join("-");
      doc.text(sanitize(`${item.label}${timeStr ? "  " + timeStr : ""}`), MARGIN + 3, y + 6.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      y += headerH + 2;

      if (item.venue) {
        const v = item.venue;
        const innerPad = 4;
        const cardW = CONTENT_W;
        const textX = MARGIN + innerPad;
        const priceLabel = v.priceLabel ? sanitize(v.priceLabel) : "";
        doc.setFontSize(10);
        const priceW = priceLabel ? doc.getTextWidth(priceLabel) + 4 : 0;
        const textW2 = cardW - innerPad * 2 - priceW;
        const blurb = sanitize(v.blurb || "");
        const blurbLines = blurb ? doc.splitTextToSize(blurb, cardW - innerPad * 2) : [];
        const subtitle = sanitize(v.subtitle || "");
        const extras: { label: string; url?: string }[] = [];
        if (v.mapsUrl) extras.push({ label: "Abrir no Google Maps >", url: v.mapsUrl });
        if (v.website) extras.push({ label: "Website >", url: v.website });
        const copyBase = data.copyBaseUrl || "https://routify-tour.com/copy";
        const cleanPhone = sanitize(v.phone || "");
        if (cleanPhone) {
          const dialUrl = `tel:${cleanPhone.replace(/\s+/g, "")}`;
          const copyUrl = `${copyBase}?text=${encodeURIComponent(cleanPhone)}&label=${encodeURIComponent(sanitize(v.name))}`;
          extras.push({ label: `Tel: ${cleanPhone}`, url: dialUrl });
          extras.push({ label: "Copiar número >", url: copyUrl });
        }
        const linkH = extras.length * 5;
        const cardH = 9 + (subtitle ? 4 : 0) + (blurbLines.length ? blurbLines.length * 4.5 + 2 : 0) + linkH + innerPad;

        if (y + cardH > PAGE_H - 20) { doc.addPage(); y = MARGIN; }

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...th.breakBorder);
        doc.roundedRect(MARGIN, y, cardW, cardH, 2, 2, "FD");

        // Name (clickable: website > maps if available)
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        const nameTarget = v.website || v.mapsUrl;
        if (nameTarget) {
          doc.setTextColor(...th.accent);
          doc.textWithLink(sanitize(v.name), textX, y + 6, { url: nameTarget, maxWidth: textW2 });
        } else {
          doc.setTextColor(15, 23, 42);
          doc.text(sanitize(v.name), textX, y + 6, { maxWidth: textW2 });
        }

        if (priceLabel) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(...th.accent);
          doc.text(priceLabel, MARGIN + cardW - innerPad, y + 6, { align: "right" });
        }

        let vy = y + 6;
        if (subtitle) {
          vy += 4;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(100, 116, 139);
          doc.text(subtitle.toUpperCase(), textX, vy);
        }
        if (blurbLines.length) {
          vy += 4;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(71, 85, 105);
          doc.text(blurbLines, textX, vy);
          vy += blurbLines.length * 4.5;
        }
        for (const ex of extras) {
          vy += 4;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...th.accent);
          const lbl = sanitize(ex.label);
          if (ex.url) {
            doc.textWithLink(lbl, textX, vy, { url: ex.url });
            const lw = doc.getTextWidth(lbl);
            doc.setDrawColor(...th.accent);
            doc.line(textX, vy + 0.8, textX + lw, vy + 0.8);
          } else {
            doc.text(lbl, textX, vy);
          }
        }

        doc.setFont("helvetica", "normal");
        doc.setTextColor(15, 23, 42);
        y += cardH + 1;
      }

      y += 2;
      continue;
    }

    const blurb = sanitize(item.blurb || "");
    const note = sanitize(item.note || "");
    doc.setFontSize(11);
    const textOffsetX = tpl === "magazine" ? MARGIN + 22 : MARGIN + 14;
    const textW = PAGE_W - MARGIN - textOffsetX - 2;
    const blurbLines = blurb ? doc.splitTextToSize(blurb, textW) : [];
    const noteLines = note ? doc.splitTextToSize(`Nota: ${note}`, textW) : [];
    const linkH = item.mapsUrl ? 9 : 0;
    // Long names must wrap inside the card instead of running under the time.
    const timeStr = sanitize([item.arrive, item.leave].filter(Boolean).join(" > "));
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const timeW = timeStr ? doc.getTextWidth(timeStr) + 4 : 0;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    const rawTitle = sanitize(item.name);
    const titleText = th.uppercase ? rawTitle.toUpperCase() : rawTitle;
    const titleLines: string[] = doc.splitTextToSize(titleText, Math.max(30, textW - timeW)).slice(0, 2);
    const extraTitleH = (titleLines.length - 1) * 5.5;
    const cardH = 12 + extraTitleH + blurbLines.length * 5 + (noteLines.length ? noteLines.length * 5 + 2 : 0) + linkH;

    // A stop card is never split across pages nor allowed to reach the footer.
    if (y + cardH > PAGE_H - 16) { doc.addPage(); y = MARGIN; }

    // Card background
    if (th.cardFill) {
      doc.setFillColor(...th.cardFill);
      doc.setDrawColor(...th.cardBorder);
      doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 2, 2, "FD");
    } else {
      doc.setDrawColor(...th.cardBorder);
      doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 2, 2, "S");
    }

    // Magazine: side accent stripe with big number
    if (tpl === "magazine") {
      doc.setFillColor(...th.accent);
      doc.rect(MARGIN, y, 14, cardH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(String(item.index), MARGIN + 7, y + cardH / 2 + 2, { align: "center" });
    } else if (tpl === "minimal") {
      // square outline badge
      doc.setDrawColor(...th.accent);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(MARGIN + 3, y + 3, 8, 8, 1, 1, "S");
      doc.setTextColor(...th.accent);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(String(item.index), MARGIN + 7, y + 8.6, { align: "center" });
    } else {
      // circle badge
      doc.setFillColor(...th.accent);
      doc.circle(MARGIN + 6, y + 7, 4, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(String(item.index), MARGIN + 6, y + 8.4, { align: "center" });
    }

    // Title
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(titleLines, textOffsetX, y + 8);

    // Time
    if (timeStr) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(timeStr, PAGE_W - MARGIN - 3, y + 8, { align: "right" });
    }

    // Blurb
    let ty = y + 14 + extraTitleH;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    if (blurbLines.length) {
      doc.text(blurbLines, textOffsetX, ty);
      ty += blurbLines.length * 5;
    }
    if (noteLines.length) {
      ty += 2;
      doc.setTextColor(...th.accent);
      doc.text(noteLines, textOffsetX, ty);
      ty += noteLines.length * 5;
    }
    if (item.mapsUrl) {
      doc.setTextColor(...th.accent);
      doc.setFontSize(9);
      const linkLabel = "Abrir no Google Maps >";
      doc.textWithLink(linkLabel, textOffsetX, ty + 4, { url: item.mapsUrl });
      const lw = doc.getTextWidth(linkLabel);
      doc.setDrawColor(...th.accent);
      doc.line(textOffsetX, ty + 4.6, textOffsetX + lw, ty + 4.6);
    }

    y += cardH + 3;
  }

  return y;
}

function drawDayMap(doc: jsPDF, day: PdfDaySection, th: Theme, y: number): number {
  const mapH = day.mapImageDataUrl ? 74 : 20;
  const x = MARGIN;
  const w = CONTENT_W;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(...th.cardBorder);
  doc.roundedRect(x, y, w, mapH, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(sanitize(`Rota do ${day.title}`), x + 4, y + 6);
  if (day.routeUrl) {
    doc.setTextColor(...th.accent);
    doc.setFontSize(8);
    doc.textWithLink("Abrir esta rota no Google Maps >", x + w - 4, y + 6, { url: day.routeUrl, align: "right" });
  }

  if (day.mapImageDataUrl) {
    doc.addImage(day.mapImageDataUrl, "JPEG", x + 4, y + 9, w - 8, mapH - 13, undefined, "FAST");
    doc.setDrawColor(...th.cardBorder);
    doc.rect(x + 4, y + 9, w - 8, mapH - 13, "S");
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(
      sanitize("Mapa deste dia indisponível neste download. Usa o link para abrir a rota."),
      x + 4,
      y + 14,
    );
  }
  doc.setFont("helvetica", "normal");
  doc.setTextColor(15, 23, 42);
  return y + mapH + 4;
}

function drawDaySections(doc: jsPDF, data: PdfData, th: Theme, tpl: PdfTemplate, startY: number): number {
  let y = startY;
  const days = data.days ?? [];
  days.forEach((day) => {
    // Title + map must stay together and legible: start a new page when the
    // remaining space cannot hold both.
    const headerH = 12;
    const mapH = day.mapImageDataUrl ? 74 : 20;
    // Header, map and the first stop card must stay on the same page.
    const needed = headerH + mapH + 42;
    if (y + needed > PAGE_H - 20) {
      doc.addPage();
      y = MARGIN;
    }

    doc.setFillColor(...th.headerBg);
    doc.roundedRect(MARGIN, y, CONTENT_W, headerH, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...th.headerText);
    doc.text(sanitize(day.title), MARGIN + 4, y + 8);
    if (day.dateLabel) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(sanitize(day.dateLabel), PAGE_W - MARGIN - 4, y + 8, { align: "right" });
    }
    doc.setTextColor(15, 23, 42);
    y += headerH + 3;

    y = drawDayMap(doc, day, th, y);

    if (day.summary) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(sanitize(day.summary), MARGIN, y + 1);
      doc.setTextColor(15, 23, 42);
      y += 6;
    }

    if (day.unmappedNames && day.unmappedNames.length) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(120, 113, 108);
      const note = `Sem coordenadas (não colocados no mapa): ${day.unmappedNames.join(", ")}`;
      const lines = doc.splitTextToSize(sanitize(note), CONTENT_W);
      doc.text(lines, MARGIN, y + 1);
      y += lines.length * 4 + 2;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
    }

    if (day.items.length === 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(sanitize("Dia livre — sem paragens planeadas."), MARGIN, y + 2);
      doc.setTextColor(15, 23, 42);
      y += 8;
    } else {
      y = drawItems(doc, day.items, data, th, tpl, y);
    }
    y += 4;
  });
  return y;
}

export async function exportItineraryPdf(data: PdfData) {
  const tpl: PdfTemplate = data.template ?? "classic";
  const th = THEMES[tpl];
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  // ---------- Header ----------
  const headerH = tpl === "minimal" ? 22 : 28;
  if (tpl === "minimal") {
    // White header with bottom rule
    doc.setTextColor(...th.headerText);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(th.title, MARGIN, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(82, 82, 91);
    const sub = [sanitize(data.city), sanitize(data.country || "")].filter(Boolean).join(", ");
    const subText = th.uppercase ? sub.toUpperCase() : sub;
    doc.text(`Roteiro · ${subText}`, MARGIN, 18);
    if (data.dateLabel || data.startTime) {
      const right = [data.dateLabel, data.startTime].filter(Boolean).join(" · ");
      doc.text(sanitize(right), PAGE_W - MARGIN, 18, { align: "right" });
    }
    doc.setDrawColor(...th.cardBorder);
    doc.line(MARGIN, headerH, PAGE_W - MARGIN, headerH);
  } else {
    doc.setFillColor(...th.headerBg);
    doc.rect(0, 0, PAGE_W, headerH, "F");
    if (tpl === "colorful") {
      // accent bar
      doc.setFillColor(...th.accent);
      doc.rect(0, headerH, PAGE_W, 2, "F");
    }
    doc.setTextColor(...th.headerText);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(tpl === "magazine" ? 20 : 18);
    doc.text(th.title, MARGIN, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const sub = [sanitize(data.city), sanitize(data.country || "")].filter(Boolean).join(", ");
    doc.text(`Roteiro em ${sub}`, MARGIN, 21);
    if (data.dateLabel || data.startTime) {
      const right = [data.dateLabel, data.startTime].filter(Boolean).join(" · ");
      doc.text(sanitize(right), PAGE_W - MARGIN, 21, { align: "right" });
    }
  }

  y = headerH + 8;
  doc.setTextColor(15, 23, 42);

  // ---------- Summary ----------
  const summaryBits = [
    data.modeLabel && `Modo: ${data.modeLabel}`,
    data.totalDistanceLabel && `Distância: ${data.totalDistanceLabel}`,
    data.totalTravelLabel && `Deslocação: ${data.totalTravelLabel}`,
    data.totalCostLabel && `Custo est.: ${data.totalCostLabel}`,
  ].filter(Boolean).join("   •   ");

  if (summaryBits) {
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(sanitize(summaryBits), MARGIN, y);
    y += 6;
  }

  // Full route link (under summary)
  if (data.fullRouteUrl) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...th.accent);
    const label = "Abrir rota completa no Google Maps >";
    doc.textWithLink(label, MARGIN, y, { url: data.fullRouteUrl });
    const lw = doc.getTextWidth(label);
    doc.setDrawColor(...th.accent);
    doc.line(MARGIN, y + 0.8, MARGIN + lw, y + 0.8);
    doc.setFont("helvetica", "normal");
    y += 6;
  }

  if (summaryBits || data.fullRouteUrl) {
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 6;
    doc.setTextColor(15, 23, 42);
  }

  if (data.days && data.days.length) {
    y = drawDaySections(doc, data, th, tpl, y);
  } else {
    y = drawRouteMap(doc, data, th, y);
    y = drawItems(doc, data.items, data, th, tpl, y);
  }

  // ---------- Recursos oficiais de mobilidade ----------
  if (data.resources && data.resources.entries.length) {
    if (y > PAGE_H - 60) { doc.addPage(); y = MARGIN; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(sanitize(data.resources.title), MARGIN, y);
    y += 6;
    let lastGroup = "";
    for (const entry of data.resources.entries) {
      if (y > PAGE_H - 24) { doc.addPage(); y = MARGIN; }
      if (entry.group !== lastGroup) {
        lastGroup = entry.group;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(sanitize(entry.group), MARGIN, y);
        y += 4.5;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      if (entry.url) {
        doc.setTextColor(...th.accent);
        doc.textWithLink(sanitize(entry.label), MARGIN + 3, y, { url: entry.url });
      } else {
        // Avisos essenciais não têm link — texto simples, sem cor de link.
        doc.setTextColor(180, 83, 9);
        doc.text(sanitize(entry.label), MARGIN + 3, y);
      }
      y += 4.2;

      if (entry.note) {
        doc.setTextColor(100, 116, 139);
        for (const line of doc.splitTextToSize(sanitize(entry.note), CONTENT_W - 6)) {
          if (y > PAGE_H - 20) { doc.addPage(); y = MARGIN; }
          doc.text(line, MARGIN + 3, y);
          y += 4;
        }
      }
    }
    if (data.resources.note) {
      if (y > PAGE_H - 24) { doc.addPage(); y = MARGIN; }
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      for (const line of doc.splitTextToSize(sanitize(data.resources.note), CONTENT_W)) {
        doc.text(line, MARGIN, y);
        y += 3.6;
      }
    }
  }

  // ---------- Footer ----------
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(sanitize(data.footer || "Gerado por Routify · routify-tour.com"), MARGIN, PAGE_H - 8);
    doc.text(`${i} / ${pages}`, PAGE_W - MARGIN, PAGE_H - 8, { align: "right" });
  }

  const safeCity = sanitize(data.city).toLowerCase().replace(/\s+/g, "-") || "routify";
  const filename = `routify-${safeCity}-${tpl}.pdf`;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  if (isMobile || inIframe) {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  doc.save(filename);
}
